import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, CalendarDays, Filter, ChevronRight } from "lucide-react";

const STATUS_STYLES = {
  draft:                  { label: "Draft",                  cls: "status-neutral" },
  submitted:              { label: "Submitted",              cls: "status-info" },
  changes_requested:      { label: "Changes Requested",      cls: "status-progress" },
  rejected:               { label: "Rejected",               cls: "status-error" },
  approved:               { label: "Approved",               cls: "status-open" },
  published:              { label: "Published",              cls: "status-published" },
  updated_after_publication: { label: "Updated After Publication", cls: "status-progress" },
  archived:               { label: "Archived",               cls: "status-neutral" },
};

export default function Schedules() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    const params = status ? { status_: status } : {};
    api.get("/schedules", { params }).then(({ data }) => setItems(data));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items, [items]);
  const canCreate = user && ["manager", "admin"].includes(user.role);

  return (
    <div className="space-y-6" data-testid="schedules-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Workforce</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Employee Schedules</h1>
          <div className="text-[color:var(--text-secondary)] text-sm mt-1">
            {user?.role === "employee"
              ? "Your published shifts"
              : user?.role === "gm"
                ? "Approve, publish, and manage weekly schedules"
                : "Create and submit weekly schedules to the General Manager"}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="hos-btn-outline flex items-center gap-2" onClick={() => nav("/schedules/week")} data-testid="weekly-view-btn">
            <CalendarDays size={16}/> Weekly View
          </button>
          {canCreate && (
            <button className="hos-btn-primary flex items-center gap-2" onClick={() => nav("/schedules/new")} data-testid="new-schedule-btn">
              <Plus size={16}/> New Schedule
            </button>
          )}
        </div>
      </div>

      <div className="hos-card p-4 flex flex-wrap items-end gap-3">
        <Filter size={16} className="text-[color:var(--gold)] mb-3"/>
        <select className="hos-input max-w-[220px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="schedules-filter">
          <option value="">All statuses</option>
          {Object.entries(STATUS_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {filtered.length === 0 && (
        <div className="hos-card p-10 text-center text-[color:var(--text-secondary)]">
          <CalendarDays className="mx-auto text-[color:var(--gold)]" size={28}/>
          <div className="mt-3">No schedules yet.</div>
        </div>
      )}

      <div className="grid gap-3">
        {filtered.map((s) => {
          const style = STATUS_STYLES[s.status] || STATUS_STYLES.draft;
          return (
            <Link key={s.id} to={`/schedules/${s.id}`} data-testid={`schedule-row-${s.id}`}
                  className="hos-card p-5 flex items-center justify-between gap-4 hover:border-[color:var(--gold)]">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-sm text-[color:var(--gold)]">{s.schedule_number}</span>
                  <span className={`status-pill ${style.cls}`} data-testid={`status-${s.id}`}>{style.label}</span>
                  <span className="text-xs text-[color:var(--text-secondary)]">Week of {s.week_start}</span>
                  {s.department && <span className="text-xs text-[color:var(--text-secondary)]">· {s.department}</span>}
                </div>
                <div className="mt-2 text-sm text-[color:var(--text-secondary)]">
                  {s.summary?.shift_count || 0} shifts · {s.summary?.employee_count || 0} employees · {s.summary?.total_hours || 0}h
                  {s.submitted_by_name && <> · submitted by {s.submitted_by_name}</>}
                </div>
              </div>
              <ChevronRight className="text-[color:var(--text-secondary)]"/>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
